import { FlareSerializerExportOptions } from '@common/flare/types/to-flare-xml-options.type';
import { ValidationOptions } from '@common/html/types/serialization-options.type';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { NodeIdAttrName, NodeIdMissingValue } from '@common/prosemirror/model/node-id';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { Serializer } from '@common/prosemirror/model/serializer';
import { environment } from '@env/environment';
import { Attrs, Fragment, MarkSpec, Node, NodeSpec, ProseMirrorNode } from 'prosemirror-model';
import { v4 as uuidv4 } from "uuid";

export abstract class FlareDataSchema extends CollabSchema {
  static DocMaxCharLength: number = environment.flareTextEditorDocMaxCharLength ? parseInt(environment.flareTextEditorDocMaxCharLength, 10) : 0;

  abstract get name(): string;
  abstract get version(): string;

  /** Gets the document serializer for the schema. */
  abstract get serializer(): Serializer;

  /** Gets the function to validate document formed from code. */
  abstract get validateDoc(): (root: any) => void;

  get docMaxCharLength(): number { return FlareDataSchema.DocMaxCharLength; }

  constructor(schemaPlugins: SchemaPlugin[], private createGuid?: () => string) {

    let nodes: Dictionary<NodeSpec> = {};
    const marks: Dictionary<MarkSpec> = {};

    // Add schema nodes and marks from the schema plugins
    schemaPlugins.forEach(plugin => {
      plugin.addNodes(nodes);
      plugin.addMarks(marks);
    });

    // Let the schema plugins modify the nodes and marks now that they are all added
    schemaPlugins.forEach(plugin => {
      plugin.modifyNodesAndMarks(nodes, marks);
    });

    super({ nodes, marks });

    // Add schema props from the schema modules
    schemaPlugins.forEach(plugin => {
      plugin.addProps(this);
    });
  }

  codeToDoc(code: string): ProseMirrorNode {
    const doc = this.serializer.codeToDoc(code, this, this.validateDoc?.bind(this));
    this.assignUniqueId(doc, true);
    return doc;
  }

  nodeToCode(node: ProseMirrorNode, options?: FlareSerializerExportOptions): string {
    return this.serializer.nodeToCode(node, this, options);
  }

  fragmentToCode(fragment: Fragment, options?: FlareSerializerExportOptions): string {
    return this.serializer.fragmentToCode(fragment, this, options);
  }

  validate(code: string, options?: ValidationOptions): Error | null {
    return this.serializer.validate(code, this, options);
  }

  createNode(type: string, attrs?: Attrs | null, content?: Node | null): Node {
    const node = this.nodes[type].create(attrs, content);
    this.assignUniqueId(node, false);
    return node;
  }

  /**
   * Assigns an unique ID value to the node if the node's ID is missing.
   * @param node The node to assign an unique ID.
   * @param traverse Specifies whether to assign unique IDs for node's children.
   */
  protected assignUniqueId(node: Node, traverse: boolean): void {
    if (!node) return;
    if (node.attrs && node.attrs[NodeIdAttrName] === NodeIdMissingValue) {
      // cast node to any to avoid TypeScript error, as Node.attrs is readonly
      (node as any).attrs[NodeIdAttrName] = this.generateNodeId();
    }
    if (traverse && node.content && node.content.size) {
      node.content.forEach(child => this.assignUniqueId(child, traverse));
    }
  }

  private generateNodeId(): string {
    return this.createGuid ? this.createGuid() : uuidv4();
  }

}
